import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      if (redirectTo) {
        navigate(redirectTo);
      } else if (!user.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, authLoading, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      
      if (redirectTo) {
        navigate(redirectTo);
      } else if (user.is_admin) {
        navigate('/admin');
      } else if (!user.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-white">
        <div className="animate-pulse text-teal text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-white flex font-body">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-block mb-12">
            <span className="font-display text-2xl text-navy">
              Cali<span className="italic-teal">Moms</span> Connect
            </span>
          </Link>

          <h1 className="font-display text-4xl text-navy mb-2">Welcome back</h1>
          <p className="text-text-mid mb-8">Sign in to your account.</p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-navy font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
                className="h-12 rounded-xl border-seafoam focus:border-teal focus:ring-teal/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-navy font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                  className="h-12 rounded-xl border-seafoam focus:border-teal focus:ring-teal/20 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-light hover:text-teal"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="text-right">
              <Link 
                to="/reset-password" 
                className="text-sm text-teal hover:text-teal-deep font-medium"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-teal hover:bg-teal-deep text-white rounded-full font-bold text-base"
            >
              {loading ? 'Signing in...' : (
                <>Sign In <ArrowRight size={18} className="ml-2" /></>
              )}
            </Button>
          </form>

          <p className="text-center text-text-mid mt-8">
            Don't have an account?{' '}
            <Link to="/signup" className="text-teal hover:text-teal-deep font-semibold" data-testid="signup-link">
              Join Now
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 bg-seafoam/30 items-center justify-center p-12">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center"
        >
          <span className="font-display text-5xl text-navy">
            Cali<span className="italic-teal">Moms</span>
          </span>
          <p className="text-text-mid mt-6 text-lg max-w-sm">
            Your village is waiting.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
